"use client";

import { usePathname } from "next/navigation";
import type React from "react";
import { useCallback, useEffect, useRef } from "react";
import {
  checkSessionStatusAction,
  type SessionStatusResult,
} from "@/actions/serverActions/auth/checkSessionStatusAction";
import { LOGOUT_ENDPOINT_INSIDE } from "@/config";
import { isPublicRoute } from "@/lib/route-config";
import {
  getIdleTimeoutMs,
  SESSION_TIMEOUT_CHANGED_EVENT,
} from "@/utils/sessionTimeout";

/** Background poll — catches server-side session deletion (logout elsewhere, admin revoke). */
const POLL_INTERVAL_MS = 30_000;
/** Floor for the exact-expiry re-check — guards against client clocks running ahead. */
const MIN_RECHECK_MS = 15_000;
/** How often the idle watcher compares now against the last activity timestamp. */
const IDLE_CHECK_MS = 15_000;
/** User-activity events that reset the idle clock (passive listeners). */
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "wheel",
] as const;

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * Client-side session watcher + idle-logout. The server-side redirect chain
 * (middleware + 401 paths) only fires on navigation — this guard covers the
 * idle tab on two fronts, both logging out through the logout endpoint (which
 * clears every auth cookie and lands on the login page):
 *
 * 1. **Server session** — polls the read-only session status, re-checks at the
 *    exact expiry moment and on tab focus, and logs out the instant the session
 *    dies (JWT/Redis expiry, admin revoke, logout elsewhere).
 * 2. **Idle timeout** — the Account → Security "session timeout after
 *    inactivity" preference (per-browser `localStorage`); logs out after that
 *    much time with no user activity. `never` disables it.
 */
export default function AuthGuard({ children }: AuthGuardProps) {
  const pathname = usePathname();
  const onPublicRoute = isPublicRoute(pathname ?? "");
  // Guards against firing the redirect twice (poll / exact timer / idle racing).
  const loggingOut = useRef(false);

  const logout = useCallback(() => {
    if (loggingOut.current) return;
    loggingOut.current = true;
    window.location.href = LOGOUT_ENDPOINT_INSIDE;
  }, []);

  // 1. Server-session watcher.
  useEffect(() => {
    if (onPublicRoute) return;

    let cancelled = false;
    let exactTimer: ReturnType<typeof setTimeout> | undefined;

    const check = async () => {
      let result: SessionStatusResult;
      try {
        result = await checkSessionStatusAction();
      } catch {
        return; // transient (network / dev-server restart) — the interval retries
      }
      if (cancelled) return;

      if (result.status === "expired") {
        logout();
        return;
      }
      if (result.status === "active") {
        // Re-check the moment the session is due to die, so logout fires in
        // real time — not up to a poll interval later.
        clearTimeout(exactTimer);
        const delay = Math.max(
          result.expiresAt - Date.now() + 1_000,
          MIN_RECHECK_MS,
        );
        exactTimer = setTimeout(() => void check(), delay);
      }
      // "unknown" → leave timers running; the next poll retries.
    };

    void check();
    const interval = setInterval(() => void check(), POLL_INTERVAL_MS);
    const onFocus = () => {
      if (document.visibilityState === "visible") void check();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(exactTimer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [onPublicRoute, logout]);

  // 2. Idle-timeout watcher (Account → Security preference).
  useEffect(() => {
    if (onPublicRoute) return;

    let timeoutMs = getIdleTimeoutMs();
    let lastActivity = Date.now();

    const markActive = () => {
      lastActivity = Date.now();
    };
    const checkIdle = () => {
      if (timeoutMs !== null && Date.now() - lastActivity >= timeoutMs) {
        logout();
      }
    };
    // Save from the Account tab updates the stored value in this same tab.
    const onPreferenceChange = () => {
      timeoutMs = getIdleTimeoutMs();
      lastActivity = Date.now();
    };
    // Returning to a tab left idle past the window logs out immediately.
    const onVisible = () => {
      if (document.visibilityState === "visible") checkIdle();
    };

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, markActive, { passive: true });
    }
    window.addEventListener(SESSION_TIMEOUT_CHANGED_EVENT, onPreferenceChange);
    document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(checkIdle, IDLE_CHECK_MS);

    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, markActive);
      }
      window.removeEventListener(
        SESSION_TIMEOUT_CHANGED_EVENT,
        onPreferenceChange,
      );
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, [onPublicRoute, logout]);

  return <>{children}</>;
}
